import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import ProductDropdown from "./ProductDropdown.jsx";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { logout, updatePatient } from '../../store/slices/authSlice';
import LanguageSwitcher from '../LanguageSwitcher.jsx';
import ProfilePopover from './ProfilePopover.jsx';

const MobileMenuItem = ({ title, onClick, isActive }) => {
    return (
        <div className="border-b">
            <button
                className={`flex justify-between items-center w-full py-2 px-4 ${isActive ? 'text-[#c8102e] font-bold' : 'text-gray-600'} hover:text-[#c8102e]`}
                onClick={onClick}
            >
                <span>{title}</span>
            </button>
        </div>
    );
};

const MegaMenu = ({ onLoginClick = () => { }, onJoinUsClick = () => { }, onOpenSignup = () => { } }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isAuthenticated, patient } = useSelector((state) => state.auth);
    const basePath = import.meta.env.VITE_BASE_PATH || '';
    const megaMenuRef = useRef(null);
    const drawerRef = useRef(null);
    const previousActiveElementRef = useRef(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
    };
    // Ensure product list expands when the current path is a product page
    useEffect(() => {
        if (pathname && pathname.startsWith('/products/')) {
            setProductsOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const menuItems = [
        { id: 'home', label: t('menu_home'), route: '/' },
        { id: 'ourProduct', label: t('menu_our_products'), route: '/' },
        { id: 'aboutUs', label: t('menu_about_us'), route: '/company/about-us' },
        { id: 'contactUS', label: t('menu_contact_us'), route: "/contact-us" },
    ];

    // Function to check if a route is active
    const isRouteActive = (route) => {
        if (route === '/') {
            return pathname === '/';
        }
        return pathname === route || pathname.startsWith(route);
    };

    const handleNavigate = (route) => {
        navigate(route);
        setActiveMenu(null);
        closeMobileMenu();
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    // Close mobile menu when route changes
    useEffect(() => {
        if (mobileMenuOpen) closeMobileMenu();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Handle ESC key and simple focus trap when mobile menu is open
    useEffect(() => {
        if (!mobileMenuOpen) return;

        const focusableSelector = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

        const getFocusable = () => {
            if (!drawerRef.current) return [];
            return Array.from(drawerRef.current.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
        };

        // Save previously focused element to restore later
        previousActiveElementRef.current = document.activeElement;

        // Focus the first focusable element inside the drawer
        const focusable = getFocusable();
        if (focusable.length) {
            focusable[0].focus();
        } else if (drawerRef.current) {
            drawerRef.current.focus();
        }

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeMobileMenu();
                return;
            }

            if (e.key === 'Tab') {
                const focusableEls = getFocusable();
                if (focusableEls.length === 0) {
                    e.preventDefault();
                    return;
                }
                const firstEl = focusableEls[0];
                const lastEl = focusableEls[focusableEls.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstEl || document.activeElement === drawerRef.current) {
                        e.preventDefault();
                        lastEl.focus();
                    }
                } else {
                    if (document.activeElement === lastEl) {
                        e.preventDefault();
                        firstEl.focus();
                    }
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            // restore focus
            try {
                if (previousActiveElementRef.current && previousActiveElementRef.current.focus) previousActiveElementRef.current.focus();
            } catch (err) {
                // ignore
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (megaMenuRef.current && !megaMenuRef.current.contains(event.target)) {
                setActiveMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [megaMenuRef]);

    return (
        <div className="sticky top-0 z-50 flex justify-center backdrop-blur-3xl bg-white/30 border-b border-white/20" ref={megaMenuRef}>
            <div className="container flex justify-between items-center py-4">
                {/* Logo */}
                <div className="flex items-center">
                    <img
                        src={`${basePath}/logo/medica-logo.png`}
                        alt="Medica Logo"
                        width={150}
                        height={50}
                        style={{ height: 'auto' }}
                        className="object-contain cursor-pointer max-md:ml-4"
                        onClick={() => handleNavigate('/')}
                    />
                </div>

                {/* Desktop Menu */}
                <nav aria-label="Main navigation" className="hidden lg:flex items-center justify-center">
                    <div className="flex gap-12 items-center justify-center">
                        {/* Home */}
                        <div className="relative group">
                            <button
                                className={`${isRouteActive('/') && !pathname.startsWith('/products/')
                                        ? 'text-gray-700 font-semibold after:absolute after:-bottom-2 after:left-0 after:w-full after:h-0.5 after:bg-[#c8102e] after:transform after:scale-x-100'
                                        : 'text-[#8C8C8C] font-semibold'
                                    } hover:text-gray-700 relative`}
                                onClick={() => handleNavigate('/')}
                            >
                                {t('menu_home')}
                            </button>
                            <div className={`absolute -bottom-2 left-0 w-full h-0.5 bg-[#c8102e] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left ${isRouteActive('/') && !pathname.startsWith('/products/') ? 'hidden' : ''
                                }`}></div>
                        </div>

                        {/* Our Products Dropdown */}
                        <div className="relative group">
                            <ProductDropdown isActive={pathname.startsWith('/products/')} />
                            <div className={`absolute -bottom-3 left-0 w-full h-0.5 bg-[#c8102e] transform ${pathname.startsWith('/products/') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                                } transition-transform duration-200 origin-left`}></div>
                        </div>

                        {menuItems.filter(item => item.id !== 'home' && item.id !== 'ourProduct').map((item) => (
                            <div key={item.id} className="relative group">
                                <button
                                    className={`${isRouteActive(item.route)
                                            ? 'text-gray-700 font-semibold after:absolute after:-bottom-2 after:left-0 after:w-full after:h-0.5 after:bg-[#c8102e] after:transform after:scale-x-100'
                                            : 'text-[#8C8C8C] font-semibold'
                                        } hover:text-gray-700 relative`}
                                    onClick={() => handleNavigate(item.route)}
                                >
                                    {item.label}
                                </button>
                                <div className={`absolute -bottom-2 left-0 w-full h-0.5 bg-[#c8102e] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left ${isRouteActive(item.route) ? 'hidden' : ''
                                    }`}></div>
                            </div>
                        ))}
                    </div>
                </nav>

                {/* Mobile Menu Button */}
                <div className="lg:hidden mr-5">
                    <button
                        className="text-gray-600 font-bold text-2xl"
                        onClick={toggleMobileMenu}
                        aria-label="Open navigation menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        ☰
                    </button>
                </div>
                <div className="max-lg:hidden lg:flex gap-4 items-center">
                    {!isAuthenticated ? (
                        <>
                            <LanguageSwitcher />
                            <button
                                onClick={onLoginClick}
                                className={`text-md w-28 h-10 rounded-xl relative group z-10 bg-[rgba(56,56,56,1)] text-white font-medium flex items-center justify-center transition hover:-translate-y-0.5 hover:shadow-md`}
                            >
                                {t('menu_login')}
                            </button>
                            {/* Join Us button temporarily hidden */}
                            {/* <button
                                onClick={onJoinUsClick}
                                className={`text-md w-28 h-10 rounded-xl relative group z-10 bg-[rgba(56,56,56,1)] text-white font-medium flex items-center justify-center transition hover:-translate-y-0.5 hover:shadow-md`}
                            >
                                {t('menu_join_us')}
                            </button> */}
                        </>
                    ) : (
                        <>
                            <LanguageSwitcher />
                            <ProfilePopover
                                patient={patient}
                                onLogout={handleLogout}
                                onChangeCustomer={(selected) => dispatch(updatePatient(selected))}
                                onOpenSignup={onOpenSignup}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Drawer (portal) */}
            {mobileMenuOpen && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed inset-0 bg-black/40 z-[9998] transition-all"
                    onClick={(e) => {
                        // close when clicking backdrop only
                        if (e.target === e.currentTarget) closeMobileMenu();
                    }}
                >
                    <div
                        ref={drawerRef}
                        role="dialog"
                        aria-modal="true"
                        tabIndex={-1}
                        className="absolute top-0 left-0 w-4/5 bg-white p-4 h-full z-[9999] shadow-2xl focus:outline-none"
                    >
                        <div className="flex justify-between items-center">
                            <span className="text-lg font-bold">{t('menu_mobile_menu')}</span>
                            <button onClick={closeMobileMenu} className="text-3xl font-bold" aria-label="Close navigation menu">×</button>
                        </div>
                        <div className="border-b my-4"></div>
                        <div>
                            {menuItems.map((item) => {
                                if (item.id === 'ourProduct') {
                                    return (
                                        <div key={item.id} className="border-b">
                                            <button
                                                className={`flex justify-between items-center w-full py-2 px-4 ${productsOpen ? 'text-[#c8102e] font-bold' : 'text-gray-600'} hover:text-[#c8102e]`}
                                                onClick={() => setProductsOpen(!productsOpen)}
                                                aria-expanded={productsOpen}
                                                aria-controls="mobile-products-list"
                                            >
                                                <span>{item.label}</span>
                                                <ChevronDown className={`transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {productsOpen && (
                                                <div id="mobile-products-list" className="pl-4 pr-4 pb-2 bg-white">
                                                    <button
                                                        className={`${isRouteActive('/products/medica-clinic') ? 'w-full text-left py-2 text-[#c8102e] font-bold' : 'w-full text-left py-2 text-gray-700 hover:text-[#c8102e]'}`}
                                                        onClick={() => { handleNavigate('/products/medica-clinic'); setProductsOpen(false); }}
                                                    >
                                                        Medica Clinic
                                                    </button>
                                                    <button
                                                        className={`${isRouteActive('/products/medica-reception') ? 'w-full text-left py-2 text-[#c8102e] font-bold' : 'w-full text-left py-2 text-gray-700 hover:text-[#c8102e]'}`}
                                                        onClick={() => { handleNavigate('/products/medica-reception'); setProductsOpen(false); }}
                                                    >
                                                        Medica Reception
                                                    </button>
                                                    <button
                                                        className={`${isRouteActive('/products/medica-patient-app') ? 'w-full text-left py-2 text-[#c8102e] font-bold' : 'w-full text-left py-2 text-gray-700 hover:text-[#c8102e]'}`}
                                                        onClick={() => { handleNavigate('/products/medica-patient-app'); setProductsOpen(false); }}
                                                    >
                                                        Medica PHR
                                                    </button>
                                                    <button
                                                        className={`${isRouteActive('/products/medica-pharmacy') ? 'w-full text-left py-2 text-[#c8102e] font-bold' : 'w-full text-left py-2 text-gray-700 hover:text-[#c8102e]'}`}
                                                        onClick={() => { handleNavigate('/products/medica-pharmacy'); setProductsOpen(false); }}
                                                    >
                                                        Medica Pharmacy
                                                    </button>
                                                    <button
                                                        className={`${isRouteActive('/products/medica-telemedicine') ? 'w-full text-left py-2 text-[#c8102e] font-bold' : 'w-full text-left py-2 text-gray-700 hover:text-[#c8102e]'}`}
                                                        onClick={() => { handleNavigate('/products/medica-telemedicine'); setProductsOpen(false); }}
                                                    >
                                                        Medica Telemedicine
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <MobileMenuItem
                                        key={item.id}
                                        title={item.label}
                                        onClick={() => handleNavigate(item.route)}
                                        isActive={isRouteActive(item.route)}
                                    />
                                );
                            })}
                        </div>
                        <div className="my-4">
                            {/* Language Switcher */}
                            <LanguageSwitcher />
                            {!isAuthenticated ? (
                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() => { onLoginClick(); closeMobileMenu(); }}
                                        className="flex-1 py-2 bg-gray-700 text-white rounded-full text-center font-semibold"
                                    >
                                        {t('menu_login')}
                                    </button>
                                    {/* Join Us button temporarily hidden */}
                                    {/* <button
                                        onClick={() => { onJoinUsClick(); closeMobileMenu(); }}
                                        className="flex-1 py-2 bg-gray-600 text-white rounded-full text-center font-semibold"
                                    >
                                        {t('menu_join_us')}
                                    </button> */}
                                </div>
                            ) : (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-[#7096F3] via-[#4181F3] to-[#C08EDB] text-white font-semibold">
                                            {patient?.FirstName?.[0]?.toUpperCase() || 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900 truncate">
                                                {[patient?.Title, patient?.FirstName, patient?.LastName]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </p>
                                            {patient?.Mobile && (
                                                <p className="text-sm text-gray-500 truncate">
                                                    {patient.Mobile}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { handleLogout(); closeMobileMenu(); }}
                                        className="w-full py-2 bg-red-600 text-white rounded-full text-center font-semibold hover:bg-red-700 transition"
                                    >
                                        {t('logout') || 'Logout'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MegaMenu;